'use client';

import { c as _c } from "react/compiler-runtime";
import type { CommentContextValue } from '@/contexts/comment.context';
import type { CommentListOutput } from '@/orpc/client';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CommentProvider } from '@/contexts/comment.context';
import { useCommentsContext } from '@/contexts/comments.context';
import { useCommentParams } from '@/hooks/use-comment-params';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { getDefaultImage } from '@/utils/get-default-image';
import { Markdown } from '../mdx/markdown';
import { CommentActions } from './comment-actions';
import { CommentMenu } from './comment-menu';
import { CommentReplies } from './comment-replies';
import { CommentReply } from './comment-reply';
type CommentProps = {
  comment: CommentListOutput['comments'][number];
};
export function Comment(props) {
  const $ = _c(57);
  const {
    comment
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [isOpenReplies, setIsOpenReplies] = useState(false);
  const commentRef = useRef(null);
  const [params] = useCommentParams();
  const t = useTranslations();
  const {
    slug
  } = useCommentsContext();
  const isHighlighted = params.reply ? params.reply === comment.id : params.comment === comment.id;
  const {
    id,
    body,
    createdAt,
    isDeleted,
    parentId,
    user: t0,
    replyCount
  } = comment;
  const {
    id: userId,
    image,
    name,
    role
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      relative: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const formattedDate = useFormattedDate(comment.createdAt, t1);
  let t2;
  let t3;
  if ($[1] !== isHighlighted) {
    t2 = () => {
      if (isHighlighted && commentRef.current) {
        const top = commentRef.current.getBoundingClientRect().top + window.scrollY - 128;
        window.scrollTo({
          top,
          behavior: "smooth"
        });
      }
    };
    t3 = [isHighlighted];
    $[1] = isHighlighted;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const hasReplies = !parentId && replyCount > 0;
  let t4;
  if ($[4] !== userId) {
    t4 = getDefaultImage(userId);
    $[4] = userId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const defaultImage = t4;
  let t5;
  if ($[6] !== comment || $[7] !== isEditing || $[8] !== isOpenReplies || $[9] !== isReplying || $[10] !== slug) {
    t5 = {
      isEditing,
      isReplying,
      isOpenReplies,
      setIsEditing,
      setIsReplying,
      setIsOpenReplies,
      slug,
      comment
    };
    $[6] = comment;
    $[7] = isEditing;
    $[8] = isOpenReplies;
    $[9] = isReplying;
    $[10] = slug;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const context = t5;
  const t6 = `comment-${id}`;
  let t7;
  if ($[12] !== isHighlighted || $[13] !== t) {
    t7 = isHighlighted && <Badge className="mb-4">{t("blog.comments.highlighted-comment")}</Badge>;
    $[12] = isHighlighted;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = image ?? defaultImage;
  let t9;
  if ($[15] !== name || $[16] !== t8) {
    t9 = <Image src={t8} alt={name} width={32} height={32} className="z-10 size-8 rounded-full" />;
    $[15] = name;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== name) {
    t10 = <div className="max-w-35 truncate font-semibold sm:max-w-none">{name}</div>;
    $[18] = name;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== createdAt || $[21] !== formattedDate) {
    t11 = <div className="text-muted-foreground">{formattedDate ? <Tooltip><TooltipTrigger><span>{formattedDate}</span></TooltipTrigger><TooltipContent>{new Date(createdAt).toLocaleString()}</TooltipContent></Tooltip> : <Skeleton className="h-4 w-24" />}</div>;
    $[20] = createdAt;
    $[21] = formattedDate;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== role || $[24] !== t) {
    t12 = role === "admin" && <div className="rounded-full border border-red-500/50 bg-red-100/50 px-2 py-0.5 text-sm dark:bg-red-900/50">{t("blog.comments.author")}</div>;
    $[23] = role;
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t12) {
    t13 = <div className="flex items-center gap-2 text-sm">{t10}{t11}{t12}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <CommentMenu />;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t13) {
    t15 = <div className="ml-0.5 flex h-8 items-center justify-between">{t13}{t14}</div>;
    $[31] = t13;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== body || $[34] !== isDeleted || $[35] !== t) {
    t16 = isDeleted ? <p className="my-3 ml-0.5 text-sm text-muted-foreground">{t("blog.comments.this-comment-has-been-deleted")}</p> : <div data-posthog-mask={true}><Markdown>{body}</Markdown></div>;
    $[33] = body;
    $[34] = isDeleted;
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== comment || $[38] !== isReplying) {
    t17 = isReplying ? <CommentReply comment={comment} /> : <CommentActions />;
    $[37] = comment;
    $[38] = isReplying;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t17) {
    t18 = <div className="min-w-0 flex-1">{t15}{t16}{t17}</div>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t18 || $[45] !== t9) {
    t19 = <div className="flex gap-4">{t9}{t18}</div>;
    $[44] = t18;
    $[45] = t9;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t19 || $[48] !== t6 || $[49] !== t7) {
    t20 = <div ref={commentRef} className="p-2.5" data-testid={t6}>{t7}{t19}</div>;
    $[47] = t19;
    $[48] = t6;
    $[49] = t7;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== hasReplies) {
    t21 = hasReplies && <CommentReplies />;
    $[51] = hasReplies;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== context || $[54] !== t20 || $[55] !== t21) {
    t22 = <CommentProvider value={context}>{t20}{t21}</CommentProvider>;
    $[53] = context;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  return t22;
}